import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

import StoryDefaultsProvider from 'context/StoryDefaultsProvider';
import { queryClient } from 'configs/queryClient';
import { darkTheme } from 'theme';
import { focusRingDecl } from 'theme/mixins';

import { router } from './routes';

const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
  }

  html,
  body,
  #root {
    height: 100%;
  }

  body {
    margin: 0;
    background-color: ${({ theme }) => theme.colors.backgroundPrimary};
    color: ${({ theme }) => theme.colors.textPrimary};
    font-family: ${({ theme }) => theme.fonts.regular};
  }

  /*
   * One keyboard-focus treatment for every interactive element, including any added
   * later. :where() zeroes its ARGUMENTS but not the trailing :focus-visible, so this
   * rule still weighs one pseudo-class — the same as a styled-component class. A control
   * that wants out therefore needs focusRingOptOutDecl, not a bare "outline: none",
   * which merely ties and loses on injection order.
   */
  :where(a, button, input, select, textarea, summary, [tabindex]):focus-visible {
    ${focusRingDecl}
  }

  /*
   * Reduced motion: state feedback still animates, because colour and opacity carry
   * information (hover, focus, selection). Movement does not, so transforms snap
   * instead of sliding. The loading spinner is deliberately left running — a frozen
   * spinner reads as a broken request, not as respect for a preference.
   */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      transition-property: color, background-color, border-color, outline-color, box-shadow, opacity !important;
    }
  }
`;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={darkTheme}>
      <GlobalStyle />
      <StoryDefaultsProvider>
        <RouterProvider router={router} />
      </StoryDefaultsProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
